import { DAY_MINUTES, type EventRepeat, type EventSettings, nextRun, runAt } from '@mogzo/shared';
import { Badge, Button, Checkbox, Field, Input, Notice, Panel, Select, Switch } from '@mogzo/ui';
import { CircleX, Plus, Rocket } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { ApiError, apiSend, type Project } from '../lib/api';
import {
  atDay,
  clockOf,
  lengthOf,
  minutesOf,
  type Schedule,
  type ScheduleEvent,
  shown,
  WEEKDAYS,
} from '../lib/schedule';
import { boardIdFrom } from './BoardForm';
import { MadeFrom } from './MadeFrom';

type Env = 'test' | 'prod';

function failure(error: unknown): string {
  return error instanceof ApiError ? error.message : 'Could not reach the API. Try again.';
}

/** A value typed in the form: JSON when it reads as JSON (2, true, "x"), otherwise text. */
function parsed(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

const dayOptions = WEEKDAYS.map((name, day) => ({ value: String(day), label: name }));
const choiceClass = (on: boolean) =>
  `flex min-h-11 flex-col gap-0.5 rounded-[6px] border px-3.5 py-2.5 text-left font-medium ${
    on ? 'border-ink shadow-[inset_0_0_0_1px_var(--color-ink)]' : 'border-control'
  } bg-card`;

/** `2026-11-06T18:00` (a datetime-local, in UTC) from an ISO time, and back. */
const toLocal = (iso: string | null) => (iso ? iso.slice(0, 16) : '');
const fromLocal = (value: string) => (value ? new Date(`${value}:00Z`).toISOString() : null);
const toDate = (iso: string | null, back = 0) =>
  iso ? new Date(Date.parse(iso) - back).toISOString().slice(0, 10) : '';

/**
 * Making or editing a live event (M37, the approved mockup's "Edit an event"): when it's on,
 * the config values it changes, and a leaderboard of its own. Test gets it at once; live
 * changes reach players at the times set, with no new version.
 */
export function EventForm({
  project,
  env,
  schedule,
  event,
  onDone,
  onTrashed,
  onCancel,
}: {
  project: Project;
  env: Env;
  schedule: Schedule;
  event: ScheduleEvent | null;
  onDone: (id: string | null) => void;
  /** After Delete: it went to Trash, in this group (for Undo). */
  onTrashed: (group: string, name: string) => void;
  onCancel: () => void;
}) {
  const weekStart = event?.repeat === 'weekly';
  const [name, setName] = useState(event?.name ?? '');
  const [id, setId] = useState(event?.id ?? '');
  const [idEdited, setIdEdited] = useState(false);
  const [repeat, setRepeat] = useState<EventRepeat>(event?.repeat ?? 'weekly');
  const [onDay, setOnDay] = useState(
    event?.onMinute != null && weekStart ? Math.floor(event.onMinute / DAY_MINUTES) : 4,
  );
  const [offDay, setOffDay] = useState(
    event?.offMinute != null && weekStart ? Math.floor(event.offMinute / DAY_MINUTES) : 0,
  );
  const [onTime, setOnTime] = useState(event?.onMinute != null ? clockOf(event.onMinute) : '18:00');
  const [offTime, setOffTime] = useState(
    event?.offMinute != null ? clockOf(event.offMinute) : '00:00',
  );
  const [first, setFirst] = useState(
    event && event.repeat !== 'once' ? toDate(event.startsAt) : '',
  );
  const [last, setLast] = useState(
    event && event.repeat !== 'once' ? toDate(event.endsAt, DAY_MINUTES * 60_000) : '',
  );
  const [onceFrom, setOnceFrom] = useState(event?.repeat === 'once' ? toLocal(event.startsAt) : '');
  const [onceUntil, setOnceUntil] = useState(event?.repeat === 'once' ? toLocal(event.endsAt) : '');
  const [values, setValues] = useState<{ key: string; text: string }[]>(
    Object.entries(event?.config ?? {}).map(([key, value]) => ({
      key,
      text: JSON.stringify(value),
    })),
  );
  const [hasBoard, setHasBoard] = useState(Boolean(event?.board));
  const [boardFrom, setBoardFrom] = useState(event?.board?.from ?? schedule.boards[0]?.id ?? '');
  const [boardName, setBoardName] = useState(event?.board?.name ?? '');
  const [enabled, setEnabled] = useState(event?.enabled ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [testRun, setTestRun] = useState(event?.run?.test ? event.run : null);
  const base = `/v1/dev/projects/${project.id}/events`;
  const where = env === 'prod' ? 'live' : 'test';

  /** The settings as typed, or a reason they can't be saved yet. */
  function settings(): EventSettings | string {
    const config: Record<string, unknown> = {};
    for (const row of values) {
      if (!row.key.trim()) continue;
      config[row.key.trim()] = parsed(row.text);
    }
    const board = hasBoard ? { from: boardFrom, name: boardName.trim() || name.trim() } : null;
    const common = { name: name.trim(), config: config as EventSettings['config'], board, enabled };
    if (repeat === 'once') {
      return {
        ...common,
        repeat,
        startsAt: fromLocal(onceFrom),
        endsAt: fromLocal(onceUntil),
        onMinute: null,
        offMinute: null,
      };
    }
    const on = minutesOf(onTime);
    const off = minutesOf(offTime);
    if (on === null || off === null) return 'Times are like 18:00.';
    const weekly = repeat === 'weekly';
    return {
      ...common,
      repeat,
      startsAt: first ? `${first}T00:00:00.000Z` : null,
      endsAt: last
        ? new Date(Date.parse(`${last}T00:00:00Z`) + DAY_MINUTES * 60_000).toISOString()
        : null,
      onMinute: weekly ? onDay * DAY_MINUTES + on : on,
      offMinute: weekly ? offDay * DAY_MINUTES + off : off,
    };
  }

  const current = settings();
  const preview = typeof current === 'string' ? null : current;
  const schedulePreview = preview && {
    repeat: preview.repeat,
    startsAt: preview.startsAt ? new Date(preview.startsAt) : null,
    endsAt: preview.endsAt ? new Date(preview.endsAt) : null,
    onMinute: preview.onMinute,
    offMinute: preview.offMinute,
  };
  const upcoming = (() => {
    if (!schedulePreview) return [];
    const now = new Date(schedule.now);
    const runs = [];
    const on = runAt(schedulePreview, now);
    if (on) runs.push(on);
    let next = nextRun(schedulePreview, now);
    while (next && runs.length < 3) {
      runs.push(next);
      next = nextRun(schedulePreview, next.start);
    }
    return runs;
  })();

  async function save(submitted: FormEvent) {
    submitted.preventDefault();
    const next = settings();
    if (typeof next === 'string') return setError(next);
    setBusy(true);
    setError(null);
    try {
      if (event) await apiSend('PUT', `${base}/${event.id}?env=${env}`, next);
      else await apiSend('POST', `${base}?env=${env}`, { id, ...next });
      setSaved(true);
      onDone(event?.id ?? id);
    } catch (failed) {
      setError(failure(failed));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!event) return;
    setBusy(true);
    try {
      const { group } = await apiSend<{ group: string }>(
        'DELETE',
        `${base}/${event.id}?env=${env}`,
      );
      onTrashed(group, event.name);
    } catch (failed) {
      setError(failure(failed));
      setBusy(false);
    }
  }

  async function test(on: boolean) {
    if (!event) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await apiSend<ScheduleEvent>('POST', `${base}/${event.id}/test-run`, { on });
      setTestRun(updated.run?.test ? updated.run : null);
    } catch (failed) {
      setError(failure(failed));
    } finally {
      setBusy(false);
    }
  }

  const weekly = repeat === 'weekly';
  const runLength =
    preview && preview.onMinute !== null && preview.offMinute !== null && preview.repeat !== 'once'
      ? lengthOf(preview.repeat, preview.onMinute, preview.offMinute)
      : '';
  const source = schedule.boards.find((b) => b.id === boardFrom);

  return (
    <div className="flex flex-wrap items-start gap-6">
      <form
        onSubmit={save}
        aria-labelledby="event-form-heading"
        className="mz-panel flex min-w-0 flex-[999_1_560px] flex-col"
      >
        <header className="mz-panel-head">
          <div>
            <h2 id="event-form-heading" className="mz-text-title-lg m-0">
              {event ? `Edit ${event.name}` : 'New event'}
            </h2>
            <p className="mz-panel-desc">
              {event ? (
                <>
                  <span className="font-mono">{event.id}</span> · in the {where} schedule.{' '}
                </>
              ) : null}
              {env === 'test'
                ? 'Your test community gets it right away. Copy it to live when it’s right.'
                : 'Players get it at the times you set. No new version needed.'}
            </p>
            {event && <MadeFrom origin={event.origin} />}
          </div>
          <Badge tone={env === 'test' ? 'info' : 'success'}>
            {env === 'test' ? 'Test schedule' : 'Live schedule'}
          </Badge>
        </header>

        <div className="mz-panel-body flex flex-col gap-7">
          {error && <Notice tone="danger">{error}</Notice>}
          {saved && !error && <Notice tone="success">Saved.</Notice>}

          <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
            <Field label="Name players see" htmlFor="ev-name">
              <Input
                id="ev-name"
                value={name}
                maxLength={60}
                onChange={(change) => {
                  setName(change.target.value);
                  if (!event && !idEdited) setId(boardIdFrom(change.target.value));
                }}
              />
            </Field>
            <Field
              label="Id your game uses"
              htmlFor="ev-id"
              hint={event ? 'Fixed once made.' : 'Made from the name. Fixed once made.'}
            >
              <Input
                id="ev-id"
                mono
                value={id}
                readOnly={Boolean(event)}
                onChange={(change) => {
                  setId(change.target.value);
                  setIdEdited(true);
                }}
              />
            </Field>
          </div>

          <fieldset className="m-0 flex flex-col gap-3 border-0 p-0">
            <legend className="mb-3 p-0 text-[13px] font-semibold">When it’s on</legend>
            <div className="grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-2.5">
              {(
                [
                  ['once', 'Once', 'Between two times'],
                  ['daily', 'Every day', 'The same hours each day'],
                  ['weekly', 'Every week', 'Like Friday to Monday'],
                ] as const
              ).map(([value, label, hint]) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={repeat === value}
                  onClick={() => setRepeat(value)}
                  className={choiceClass(repeat === value)}
                >
                  {label}
                  <span className="text-[13px] font-normal text-muted">{hint}</span>
                </button>
              ))}
            </div>
            {repeat === 'once' ? (
              <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-4">
                <Field label="Turns on" htmlFor="ev-from" hint="UTC.">
                  <Input
                    id="ev-from"
                    type="datetime-local"
                    value={onceFrom}
                    onChange={(c) => setOnceFrom(c.target.value)}
                  />
                </Field>
                <Field
                  label="Turns off"
                  htmlFor="ev-until"
                  hint="UTC. Up to 90 days after it turns on."
                >
                  <Input
                    id="ev-until"
                    type="datetime-local"
                    value={onceUntil}
                    onChange={(c) => setOnceUntil(c.target.value)}
                  />
                </Field>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-4">
                  {(
                    [
                      ['Turns on', onDay, setOnDay, onTime, setOnTime, 'ev-on'],
                      ['Turns off', offDay, setOffDay, offTime, setOffTime, 'ev-off'],
                    ] as const
                  ).map(([label, day, setDay, time, setTime, key]) => (
                    <Field key={key} label={label} htmlFor={`${key}-time`}>
                      <div
                        className={
                          weekly ? 'grid grid-cols-[minmax(0,1fr)_96px] gap-2' : 'max-w-[120px]'
                        }
                      >
                        {weekly && (
                          <Select
                            aria-label={`${label}: day`}
                            options={dayOptions}
                            value={String(day)}
                            onChange={(change) => setDay(Number(change.target.value))}
                          />
                        )}
                        <Input
                          id={`${key}-time`}
                          mono
                          value={time}
                          onChange={(change) => setTime(change.target.value)}
                        />
                      </div>
                    </Field>
                  ))}
                </div>
                <p className="mz-hint m-0">
                  {runLength ? `Lasts ${runLength}. ` : ''}Times are UTC.
                </p>
                <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-4">
                  <Field label="First day" optional htmlFor="ev-first" hint="Empty: from now on.">
                    <Input
                      id="ev-first"
                      type="date"
                      value={first}
                      onChange={(c) => setFirst(c.target.value)}
                    />
                  </Field>
                  <Field
                    label="Last day"
                    optional
                    htmlFor="ev-last"
                    hint="Empty: until you turn it off."
                  >
                    <Input
                      id="ev-last"
                      type="date"
                      value={last}
                      onChange={(c) => setLast(c.target.value)}
                    />
                  </Field>
                </div>
              </>
            )}
          </fieldset>

          <fieldset className="m-0 flex flex-col gap-2.5 border-0 p-0">
            <legend className="mb-1 p-0 text-[13px] font-semibold">While it’s on</legend>
            <p className="mz-hint m-0">
              These config values change while the event is on. Nothing else does, and when it ends
              they go back to your config’s own.
            </p>
            {values.length > 0 && (
              <div className="mz-table-wrap rounded-[8px] border border-line">
                <table className="mz-table">
                  <thead>
                    <tr>
                      <th scope="col">Value</th>
                      <th scope="col">In {where} config</th>
                      <th scope="col">While it’s on</th>
                      <th scope="col" style={{ width: 44 }}>
                        <span className="sr-only">Remove</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {values.map((row, index) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: rows have no id until saved
                      <tr key={index}>
                        <td>
                          <Input
                            aria-label="Value name"
                            mono
                            value={row.key}
                            onChange={(change) =>
                              setValues(
                                values.map((r, i) =>
                                  i === index ? { ...r, key: change.target.value } : r,
                                ),
                              )
                            }
                          />
                        </td>
                        <td className="text-muted">
                          <span className="mz-mono">
                            {row.key in schedule.config
                              ? shown(schedule.config[row.key])
                              : 'not set'}
                          </span>
                        </td>
                        <td>
                          <Input
                            aria-label={`${row.key || 'Value'} while it’s on`}
                            mono
                            value={row.text}
                            onChange={(change) =>
                              setValues(
                                values.map((r, i) =>
                                  i === index ? { ...r, text: change.target.value } : r,
                                ),
                              )
                            }
                          />
                        </td>
                        <td>
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={CircleX}
                            label={`Remove ${row.key || 'this value'}`}
                            onClick={() => setValues(values.filter((_, i) => i !== index))}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Button
                variant="ghost"
                size="sm"
                icon={Plus}
                onClick={() => setValues([...values, { key: '', text: '' }])}
              >
                Add a value
              </Button>
              <span className="mz-hint">Values are JSON: 2, "night", true.</span>
            </div>
            <p className="mz-hint m-0">
              If two events change the same value at once, the one that turned on last wins.
            </p>
          </fieldset>

          <fieldset className="m-0 flex flex-col gap-3 border-0 p-0">
            <legend className="mb-3 p-0 text-[13px] font-semibold">Leaderboard</legend>
            <Checkbox
              checked={hasBoard}
              disabled={!schedule.boards.length}
              onChange={(change) => setHasBoard(change.target.checked)}
              label={`Give each ${repeat === 'weekly' ? 'weekend' : 'run'} a leaderboard of its own`}
              hint={
                schedule.boards.length
                  ? 'Scores your game sends to the board below while the event is on count here too. Each run starts fresh; past ones stay to look back on.'
                  : `The ${where} schedule has no leaderboards to count scores from yet.`
              }
            />
            {hasBoard && (
              <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4 pl-7">
                <Field
                  label="Count scores from"
                  htmlFor="ev-board"
                  hint="Its order, format and limits are copied."
                >
                  <Select
                    id="ev-board"
                    options={schedule.boards.map((b) => ({
                      value: b.id,
                      label: `${b.name} · ${b.sort === 'desc' ? 'highest first' : 'lowest first'}`,
                    }))}
                    value={boardFrom}
                    onChange={(change) => setBoardFrom(change.target.value)}
                  />
                </Field>
                <Field
                  label="Board name players see"
                  htmlFor="ev-board-name"
                  hint={`Its id is the event’s, ${id || 'the id above'}.`}
                >
                  <Input
                    id="ev-board-name"
                    value={boardName}
                    placeholder={name || source?.name}
                    onChange={(change) => setBoardName(change.target.value)}
                  />
                </Field>
              </div>
            )}
          </fieldset>

          <div className="flex flex-col gap-1.5">
            <Switch
              label="On"
              checked={enabled}
              onChange={(change) => setEnabled(change.target.checked)}
            />
            <p className="mz-hint m-0">Turn it off to skip its runs without deleting it.</p>
          </div>
        </div>

        <footer className="mz-panel-foot bg-card">
          <div className="flex flex-wrap gap-2.5">
            <Button type="submit" variant="primary" loading={busy}>
              {event ? 'Save changes' : 'Create event'}
            </Button>
            <Button onClick={onCancel} disabled={busy}>
              Cancel
            </Button>
          </div>
          {event && (
            <Button variant="danger" onClick={remove} disabled={busy}>
              Delete event
            </Button>
          )}
        </footer>
      </form>

      <div className="flex max-w-[400px] min-w-0 flex-[1_1_300px] flex-col gap-6">
        <Panel
          title={repeat === 'weekly' ? 'Next weekends' : 'Next runs'}
          description="As set now. Times are UTC."
          flush
        >
          {upcoming.length ? (
            <ul className="m-0 list-none p-0">
              {upcoming.map((run) => (
                <li
                  key={run.start.toISOString()}
                  className="flex flex-col gap-0.5 border-t border-line px-5 py-3 first:border-t-0"
                >
                  <span className="font-medium">{atDay(run.start.toISOString())}</span>
                  <span className="text-[13px] text-muted">
                    until {atDay(run.end.toISOString())}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="m-0 px-5 py-4 text-[13px] text-muted">
              {typeof current === 'string' ? current : 'No runs ahead as set.'}
            </p>
          )}
        </Panel>

        {event && env === 'test' && (
          <Panel
            title="Try it in test"
            description={
              testRun
                ? `On for testers until ${atDay(testRun.end)}.`
                : `Turn it on now for your testers, so you don’t wait${repeat === 'weekly' ? ' for the weekend' : ''}. It turns off when you end it, or after an hour.`
            }
          >
            {testRun ? (
              <Button onClick={() => test(false)} loading={busy}>
                End now
              </Button>
            ) : (
              <Button icon={Rocket} onClick={() => test(true)} loading={busy}>
                Turn on now
              </Button>
            )}
          </Panel>
        )}

        <Panel title="In your game">
          <pre className="m-0 rounded-[6px] border border-line bg-field px-3.5 py-3 font-mono text-[12.5px] leading-5 whitespace-pre-wrap">
            {`// Values come with the config.\nconst config = await mogzo.config.get();\n${
              hasBoard && source
                ? `\n// Keep sending scores as now.\nmogzo.leaderboards.submit(\n  '${source.id}', score);\n\n// Show the event's own board.\nconst [event] =\n  await mogzo.events.active();\nif (event?.leaderboard) {\n  const { entries } = await\n    mogzo.leaderboards.top(\n      event.leaderboard);\n}`
                : `\n// Which events are on, for a banner.\nconst events =\n  await mogzo.events.active();`
            }`}
          </pre>
        </Panel>
      </div>
    </div>
  );
}
